import ManropeBoldUrl from 'assets/Manrope-Bold.ttf'
import ManropeRegularUrl from 'assets/Manrope-Regular.ttf'
import { jsPDF } from 'jspdf'
import { KVAnnotation } from 'types'

/**
 * Convert annotations to PDF.
 *
 * @param annotations - Array of KV annotations to export
 * @param metadata - Optional metadata (component name, site name, organization)
 * @returns Promise resolving to PDF blob
 */
export async function convertAnnotationsToPDF(
  annotations: KVAnnotation[] | undefined,
  metadata?: {
    componentName?: string
    siteName?: string
    organizationName?: string
  }
): Promise<Blob> {
  const decimalPlaces = 3

  if (!annotations || annotations.length === 0) {
    return await generateEmptyAnnotationsPDF(metadata)
  }

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)
  generateAnnotationsSummary(pdf, annotations)

  // Start annotations on a new page
  pdf.addPage()
  await generateBlackBarAndLogo(pdf)

  await generateAnnotationsContent(pdf, annotations, decimalPlaces)
  generatePDFFooter(pdf)

  return pdf.output('blob')
}

function convertSvgToPngDataUrl(size: number = 48): Promise<string> {
  return new Promise((resolve, reject) => {
    // SVG with white fill for the logo
    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 295.11 295.11" width="${size}" height="${size}">
        <g>
          <path fill="white" d="m139.88,105.79c-12.03,0-20.66,8.4-20.66,19.75s8.85,19.75,20.66,19.75,20.88-8.4,20.88-19.75-9.08-19.75-20.88-19.75Z"/>
          <path fill="white" d="m0,0v295.11h295.11V0H0Zm210.62,89.44h-13.99c-5.45,0-9.76,2.72-10.9,7.04,5.68,8.17,9.08,17.93,9.08,29.06,0,29.28-23.61,51.08-54.94,51.08-5.9,0-11.58-.68-17.03-2.27-3.4,2.27-5.22,5.45-5.22,8.85,0,5.22,4.31,8.63,10.44,8.63h36.09c26,0,46.45,19.15,46.45,44.49h-36.24c-.91-7.49-7.26-12.49-16.57-12.49h-36.09c-22.47,0-37.23-12.94-37.23-32.23,0-12.26,6.13-24.06,15.89-29.96-9.53-9.08-15.21-21.79-15.21-36.09,0-29.28,23.38-51.08,54.71-51.08,8.85,0,17.03,1.82,24.29,4.77,3.41-12.49,14.98-20.43,30.87-20.43h15.58v30.65Z"/>
        </g>
      </svg>
    `

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = size
    canvas.height = size

    const img = new Image()
    const svgBlob = new Blob([svgContent], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      if (ctx) {
        ctx.drawImage(img, 0, 0, size, size)
        const dataUrl = canvas.toDataURL('image/png', 1.0)
        URL.revokeObjectURL(url)
        resolve(dataUrl)
      } else {
        reject(new Error('Failed to get canvas context'))
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG image'))
    }

    img.src = url
  })
}

async function generateBlackBarAndLogo(pdf: jsPDF): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const blackBarHeight = 20

  // Draw black bar at the top
  pdf.setFillColor(0, 0, 0)
  pdf.rect(0, 0, pageWidth, blackBarHeight, 'F')

  // Add Gecko logo
  try {
    const logoDataUrl = await convertSvgToPngDataUrl(144) // Higher resolution
    const logoSize = 12 // Size in mm
    pdf.addImage(logoDataUrl, 'PNG', 10, 4, logoSize, logoSize)
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error)
  }

  // Add Gecko text next to logo
  pdf.setTextColor(255, 255, 255)
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('GECKO ROBOTICS', 25, 12)

  // Add "Defect Report" to the right side of the black bar
  const defectReportText = 'Defect Report'
  const defectReportWidth = pdf.getTextWidth(defectReportText)
  pdf.text(defectReportText, pageWidth - defectReportWidth - 10, 12)

  // Reset text color for subsequent content
  pdf.setTextColor(0, 0, 0)
}

export async function generatePDFHeader(
  pdf: jsPDF,
  metadata?: {
    componentName?: string
    siteName?: string
    organizationName?: string
  }
): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const timestamp = new Date().toLocaleString()

  // Add black bar and logo at the top
  await generateBlackBarAndLogo(pdf)

  // Title
  pdf.setFontSize(20)
  pdf.setFont('Manrope', 'bold')
  const date = new Date().toLocaleDateString()
  const componentName = metadata?.componentName || 'Unknown Component'
  const title = `${date}: ${componentName} Defect Report`
  const titleWidth = pdf.getTextWidth(title)
  const titleX = (pageWidth - titleWidth) / 2
  pdf.text(title, titleX, 35)

  // Metadata section
  pdf.setFontSize(10)
  pdf.setFont('Manrope', 'normal')
  let yPosition = 45

  if (metadata?.organizationName) {
    pdf.text(`Organization: ${metadata.organizationName}`, 20, yPosition)
    yPosition += 5
  }

  if (metadata?.siteName) {
    pdf.text(`Site: ${metadata.siteName}`, 20, yPosition)
    yPosition += 5
  }

  if (metadata?.componentName) {
    pdf.text(`Component: ${metadata.componentName}`, 20, yPosition)
    yPosition += 5
  }

  pdf.text(`Generated: ${timestamp}`, 20, yPosition)
  yPosition += 5
  pdf.text('Source: Cantilever', 20, yPosition)

  // Add a line separator
  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition + 5, pageWidth - 20, yPosition + 5)
}

function generateAnnotationsSummary(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  startY?: number
): void {
  const pageWidth = pdf.internal.pageSize.getWidth()
  let yPosition = startY || 90

  // Summary section
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('Summary', 20, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setFont('Manrope', 'normal')
  const count = annotations.length
  const countText = `Total Defects: ${count}`
  pdf.text(countText, 20, yPosition)
  yPosition += 10

  // Add a line separator
  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition, pageWidth - 20, yPosition)
}

async function generateAnnotationsContent(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  decimalPlaces: number
): Promise<void> {
  let yPosition = 35
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const lineHeight = 6
  const sectionSpacing = 12

  for (let index = 0; index < annotations.length; index++) {
    const annotation = annotations[index]

    // Calculate space needed for this annotation
    const estimatedTextHeight = 80 // Rough estimate for text content
    const spaceNeeded = estimatedTextHeight + sectionSpacing

    // Check if we need a new page
    if (yPosition + spaceNeeded > pageHeight - 20) {
      pdf.addPage()
      await generateBlackBarAndLogo(pdf)
      yPosition = 35
    }

    // Annotation header
    pdf.setFontSize(14)
    pdf.setFont('Manrope', 'bold')
    const annotationTitle = `Defect ${index + 1}`
    pdf.text(annotationTitle, 20, yPosition)
    yPosition += lineHeight + 2

    // Annotation details
    pdf.setFontSize(10)
    pdf.setFont('Manrope', 'normal')

    if (annotation.label) {
      const labelText = `Label: ${annotation.label}`
      const labelLines = splitTextToFitWidth(pdf, labelText, pageWidth - 50)
      labelLines.forEach((line) => {
        pdf.text(line, 25, yPosition)
        yPosition += lineHeight
      })
    }

    if (annotation.notes) {
      pdf.text('Notes:', 25, yPosition)
      yPosition += lineHeight
      const notesLines = splitTextToFitWidth(
        pdf,
        annotation.notes,
        pageWidth - 50
      )
      notesLines.forEach((line) => {
        pdf.text(line, 30, yPosition)
        yPosition += lineHeight
      })
    }

    if (annotation.geometry?.kind === 'point' && annotation.geometry.position) {
      const positionText = formatPositionText(
        annotation.geometry.position.toString(),
        decimalPlaces
      )
      pdf.text(`Position: ${positionText}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (annotation.severity) {
      pdf.text(`Severity: ${annotation.severity}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (annotation.type) {
      pdf.text(`Type: ${annotation.type}`, 25, yPosition)
      yPosition += lineHeight
    }

    // Add spacing between annotations
    yPosition += sectionSpacing
  }
}

export function generatePDFFooter(pdf: jsPDF): void {
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageCount = pdf.getNumberOfPages()

  // Add footer to each page
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)

    // Add black line above footer
    pdf.setDrawColor(0, 0, 0) // Black color
    pdf.setLineWidth(0.5)
    pdf.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20)

    pdf.setFontSize(8)
    pdf.setFont('Manrope', 'normal')

    // Page number
    const pageText = `Page ${i} of ${pageCount}`
    const pageTextWidth = pdf.getTextWidth(pageText)
    pdf.text(pageText, pageWidth - pageTextWidth - 20, pageHeight - 10)

    // Company info
    pdf.text('Generated by Cantilever - Gecko Robotics', 20, pageHeight - 10)
  }
}

async function generateEmptyAnnotationsPDF(metadata?: {
  componentName?: string
  siteName?: string
  organizationName?: string
}): Promise<Blob> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)

  // Empty content message
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'normal')
  pdf.text('No annotations available for export.', 20, 90)

  generatePDFFooter(pdf)

  return pdf.output('blob')
}

export function formatPositionText(
  position: string,
  decimalPlaces: number
): string {
  try {
    // Parse position string format "X=-2.512 Y=213.841 Z=252.967"
    const xMatch = position.match(/X=([-\d.]+)/)
    const yMatch = position.match(/Y=([-\d.]+)/)
    const zMatch = position.match(/Z=([-\d.]+)/)

    if (xMatch && yMatch && zMatch) {
      const x = parseFloat(xMatch[1]).toFixed(decimalPlaces)
      const y = parseFloat(yMatch[1]).toFixed(decimalPlaces)
      const z = parseFloat(zMatch[1]).toFixed(decimalPlaces)
      return `X=${x}, Y=${y}, Z=${z}`
    }
  } catch (error) {
    console.warn('Failed to parse position string:', position, error)
  }
  return position
}

export function splitTextToFitWidth(
  pdf: jsPDF,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  words.forEach((word) => {
    const testLine = currentLine + (currentLine ? ' ' : '') + word
    const testWidth = pdf.getTextWidth(testLine)

    if (testWidth > maxWidth && currentLine) {
      lines.push(currentLine)
      currentLine = word
    } else {
      currentLine = testLine
    }
  })

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

export async function addManropeFont(pdf: jsPDF): Promise<void> {
  const [regularBase64, boldBase64] = await Promise.all([
    convertTtfToBase64(ManropeRegularUrl),
    convertTtfToBase64(ManropeBoldUrl),
  ])

  pdf.addFileToVFS('Manrope-Regular.ttf', regularBase64)
  pdf.addFont('Manrope-Regular.ttf', 'Manrope', 'normal')
  pdf.addFileToVFS('Manrope-Bold.ttf', boldBase64)
  pdf.addFont('Manrope-Bold.ttf', 'Manrope', 'bold')
}

async function convertTtfToBase64(fontUrl: string): Promise<string> {
  const response = await fetch(fontUrl)
  const arrayBuffer = await response.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)
  const binaryString = Array.from(bytes, (byte) =>
    String.fromCharCode(byte)
  ).join('')
  return btoa(binaryString)
}

// Utility function to draw a checkbox
export const drawCheckbox = (
  pdf: jsPDF,
  x: number,
  y: number,
  checked: boolean
) => {
  pdf.setLineWidth(0.5)
  pdf.rect(x, y, 4, 4)
  if (checked) {
    pdf.setFillColor(0, 0, 0)
    pdf.rect(x + 1, y + 1, 2, 2, 'F')
  }
}
